/**
 * Shared event lifecycle utilities.
 * Used across all screens to determine if an event is upcoming or in its check-in window.
 */

// Default event duration when no explicit end_date. End dates were removed
// from the product on 2026-08-19 (events are created with start_date only),
// so this default IS the check-in window for all new events — 4 hours covers
// typical campus events (a 1h default was cutting off QR check-in mid-event).
// Events with a legacy end_date still honor it. attendanceService's check-in
// validation imports isEventInOTPWindow below, so client UI and the
// service-enforced window derive from this one constant by construction.
const EVENT_DEFAULT_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

// How long an event stays "upcoming" after it starts, independent of the
// QR check-in window above. isEventUpcoming derives from this constant
// directly off start_date — it does NOT use getEventEndTime/EVENT_DEFAULT_DURATION_MS.
const EVENT_UPCOMING_WINDOW_MS = 60 * 60 * 1000; // 1 hour

// Firestore Timestamp | Date | ISO string | millis → Date, or null.
export function toDate(value) {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Returns the moment an event ends: its explicit (legacy) end_date if set,
 * otherwise start_date + the default 4-hour event duration.
 */
export function getEventEndTime(event) {
  const startTime = toDate(event?.start_date);
  if (!startTime) return null;
  const endTime = toDate(event?.end_date);
  return endTime || new Date(startTime.getTime() + EVENT_DEFAULT_DURATION_MS);
}

/**
 * Returns true if the event should still be treated as "upcoming"/active.
 * An event is upcoming for 1 hour after start; the QR check-in window
 * (isEventInOTPWindow) is separate and stays 4h.
 */
export function isEventUpcoming(event) {
  const startTime = toDate(event?.start_date);
  if (!startTime) return false;
  return new Date() < new Date(startTime.getTime() + EVENT_UPCOMING_WINDOW_MS);
}

/**
 * Returns true if the event is currently in its check-in window: started,
 * but not yet past its active window (see getEventEndTime). Gates QR
 * check-in validity and "happening now" UI state.
 */
export function isEventInOTPWindow(event) {
  const startTime = toDate(event?.start_date);
  const endTime = getEventEndTime(event);
  if (!startTime || !endTime) return false;
  const now = new Date();
  return now >= startTime && now < endTime;
}

/**
 * Local calendar-day key (not UTC) so events near midnight group under the
 * day they actually occur on for the viewer. Accepts a Firestore Timestamp
 * or an ISO string/Date, matching this module's start_date handling above.
 */
export function dayKey(value) {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// Lower bound (ISO-8601 string) for a "future events" server query on
// start_date: isEventUpcoming keeps an event for EVENT_UPCOMING_WINDOW_MS after
// it starts (end_date is not consulted), so anything that started earlier is
// filtered out anyway. A tight bound matters because the query's limit is
// applied before the client-side isEventUpcoming cut.
export function futureEventsCutoffISO(nowMs = Date.now()) {
  return new Date(nowMs - EVENT_UPCOMING_WINDOW_MS).toISOString();
}

// An event's start in millis; a missing or unreadable start_date is Infinity,
// so undated events sort last everywhere (one rule for every event list).
export function eventStartMs(event) {
  return toDate(event?.start_date)?.getTime() ?? Infinity;
}

// Soonest first; undated events last. Safe with Infinity (no NaN from a - b).
export function compareByStart(a, b) {
  const da = eventStartMs(a);
  const db = eventStartMs(b);
  return da === db ? 0 : da < db ? -1 : 1;
}

// Latest first (past-event lists); undated events still last.
export function compareByStartDesc(a, b) {
  const da = eventStartMs(a);
  const db = eventStartMs(b);
  if (da === db) return 0;
  if (da === Infinity) return 1;
  if (db === Infinity) return -1;
  return da > db ? -1 : 1;
}

// A new array sorted soonest first, undated last.
export function sortByStart(events) {
  return [...(events || [])].sort(compareByStart);
}

// One visibility / lifecycle / shape / sort policy for event lists (used by
// EventService.getEvents, getEventsForClubs and ClubDetails' cached payload).
// `events` are { id, ...data } rows. canSeePrivate(event) says whether the
// viewer may see a non-public event (club membership). Returns a new array,
// soonest first; rows with an unreadable start_date sort last.
export function selectVisibleEvents(events, { futureOnly = false, canSeePrivate = () => false } = {}) {
  return (events || [])
    .filter((e) => (!futureOnly || isEventUpcoming(e)) && (e.is_public || canSeePrivate(e)))
    .map((e) => ({ ...e, attendee_count: e.attendee_count || 0 }))
    .sort(compareByStart);
}
